/**
 * The client-side size rule for a form attachment, and the German message that explains a refusal (#779).
 *
 * ### Why a client limit exists at all, and why it is 10 MB
 * A submitted form is one multipart request, and it passes three caps before it is stored:
 *
 * | Hop | Cap | Where |
 * | --- | --- | --- |
 * | this check | **10 MB per file** | {@link MAX_ATTACHMENT_BYTES} |
 * | Forms service, per file | 25 MB | `SubmissionController.MaxFileBytes` |
 * | Forms service, per request | 64 MB | `SubmissionController.MaxMultipartRequestBytes` |
 *
 * 10 MB is deliberately the **tightest** of the three, so the client check always fires first. That is the whole
 * point: every hop above it refuses a request the browser cannot read a usable status from — the edge proxy's own
 * `413` carries no CORS header (CORS lives only at the gateway), so Angular sees a network failure with no status
 * and the employee gets the catch-all "konnte nicht gesendet werden" banner with nothing to act on. That is
 * Bug #779. A refusal decided here, the instant the file is picked, names the file and the limit instead.
 *
 * Raising the server caps does **not** mean raising this one: it may only move up to stay below the smallest
 * server-side cap on the path (today `MaxFileBytes`, 25 MB), and a form can easily carry five attachments, so a
 * per-file value well under the 64 MB request cap is also what keeps a full form from failing as a whole.
 *
 * The same pattern (a mirrored client cap with its own readable message) is used for floor-plan backgrounds —
 * see `BACKGROUND_MAX_BYTES` in `table-layout.model.ts`.
 */

/** Largest single attachment the runtime form accepts (10 MB). See this module's header for why it is 10 and not 25. */
export const MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024;

/** The limit as whole megabytes, for the message — derived so the copy can never drift from the constant. */
const MAX_ATTACHMENT_MB = MAX_ATTACHMENT_BYTES / 1024 / 1024;

/**
 * The Angular error key an oversized pick is stamped under, so {@link FieldRendererComponent}'s error row can show
 * its message verbatim — the same arrangement as `SERVER_ERROR_KEY` in `submission-errors.ts`.
 */
export const FILE_TOO_LARGE_KEY = 'fileTooLarge';

/**
 * A file size as German copy, e.g. `12,4 MB`.
 *
 * Rounded **up**, never down: a file of 10.04 MB formatted with `toFixed` reads "10,0 MB", which next to
 * "höchstens 10 MB" looks like the app refusing a file that meets the limit. Up is the only direction that keeps
 * the sentence self-consistent.
 */
export function formatMegabytes(bytes: number): string {
  const megabytes = Math.ceil((bytes / 1024 / 1024) * 10) / 10;
  return `${megabytes.toFixed(1).replace('.', ',')} MB`;
}

/**
 * The message shown when a picked file is too large — German, Sie form, naming the file and its real size.
 *
 * Exported so the spec and the Storybook story render the very string the component does, rather than a copy of it
 * that can drift.
 *
 * The quoting is the house style used elsewhere in the German copy (`„Bestätigen & Absenden“`, `„Unternehmensinhaber“`): the
 * German opening and closing pair, not an ASCII straight quote.
 */
export function attachmentTooLargeMessage(file: { name: string; size: number }): string {
  return `„${file.name}“ ist ${formatMegabytes(file.size)} groß. `
    + `Bitte wählen Sie eine Datei mit höchstens ${MAX_ATTACHMENT_MB} MB.`;
}

/** True when a picked file exceeds {@link MAX_ATTACHMENT_BYTES} and must be refused before the submit. */
export function isAttachmentTooLarge(file: { size: number }): boolean {
  return file.size > MAX_ATTACHMENT_BYTES;
}
